import { useEffect, useState, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Me, type SupportTicket } from '../lib/api';
import CiscoStatusCallout from '../components/CiscoStatusCallout';

const CATEGORIES = ['bug', 'access', 'config', 'how_to', 'performance', 'other'];

function statusPill(s: SupportTicket['status']) {
  if (s === 'applied' || s === 'closed') return <span className="pill-ok">{s}</span>;
  if (s === 'rejected') return <span className="pill-err">rejected</span>;
  if (s === 'approved') return <span className="pill-ok">approved</span>;
  return <span className="pill-warn">{s.replace('_', ' ')}</span>;
}

function sourceBadge(src: SupportTicket['source']) {
  const map: Record<SupportTicket['source'], string> = {
    human: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
    qa_radar: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
    system: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  };
  return <span className={`pill ${map[src]}`}>{src}</span>;
}

export default function Support({ me }: { me: Me }) {
  const [list, setList] = useState<SupportTicket[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const focusId = params.get('id');
  const [selected, setSelected] = useState<SupportTicket | null>(null);

  async function load() {
    try { setErr(null); const r = await api.ticketsList({ limit: 50 }); setList(r.tickets); }
    catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (focusId && list) setSelected(list.find((t) => t.ticket_id === focusId) ?? null);
  }, [focusId, list]);

  async function submit(input: { title: string; description: string; category: string }) {
    setBusy(true); setErr(null);
    try { await api.ticketsCreate(input); setShowForm(false); await load(); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function decide(t: SupportTicket, action: 'approve' | 'reject' | 'close') {
    setBusy(true);
    try { await api.ticketsDecide(t.ticket_id, action); await load(); setSelected(null); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function scanQa() {
    setBusy(true);
    try { const r = await api.supportScanQa(); alert(`Scanned ${r.scanned} QA findings, opened ${r.opened} new tickets.`); await load(); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Support tickets</h1>
          <p className="text-gray-500 text-sm mt-1">
            AI diagnoses every ticket and proposes a fix. Anything touching access or permissions needs super-admin approval.
            QA Sentry findings (severity ≥ error) auto-open here every 5 minutes.
          </p>
        </div>
        <div className="flex gap-2">
          {me.role === 'super_admin' && (
            <button onClick={scanQa} disabled={busy} className="btn-ghost text-sm">Scan QA now</button>
          )}
          <button onClick={() => setShowForm(true)} className="btn-primary">+ New ticket</button>
        </div>
      </div>

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}

      {/* Step 0 of any WxC/WxCC troubleshooting: check Cisco's status feed.
          Shown above the new-ticket form so operators see it before filing. */}
      <CiscoStatusCallout scope="both" />

      {showForm && <NewTicketForm onSubmit={submit} onCancel={() => setShowForm(false)} busy={busy} />}

      {list === null && <div className="card p-8 text-center text-gray-500">Loading…</div>}
      {list && list.length === 0 && (
        <div className="card p-8 text-center text-gray-500">
          No tickets yet. Open the first one — AI will diagnose it on submit.
        </div>
      )}
      <div className="space-y-3">
        {list?.map((t) => (
          <div key={t.ticket_id} onClick={() => setSelected(t)}
               className="card p-4 cursor-pointer hover:border-tnh-accent transition">
            <div className="flex items-center gap-2 flex-wrap">
              {statusPill(t.status)}
              {sourceBadge(t.source)}
              <span className="pill bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">{t.category}</span>
              {t.needs_super_admin === 1 && <span className="pill-warn">needs super-admin</span>}
              <span className="text-xs text-gray-500">· by {t.created_by_email}</span>
            </div>
            <div className="font-medium mt-2">{t.title}</div>
            {t.ai_diagnosis && <div className="text-sm text-gray-500 mt-1 line-clamp-2">{t.ai_diagnosis}</div>}
          </div>
        ))}
      </div>

      {selected && (
        <TicketModal me={me} ticket={selected} onClose={() => setSelected(null)} onDecide={decide} busy={busy} />
      )}
    </div>
  );
}

function NewTicketForm({ onSubmit, onCancel, busy }: { onSubmit: (i: { title: string; description: string; category: string }) => void; onCancel: () => void; busy: boolean }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('bug');
  function handle(e: FormEvent) { e.preventDefault(); onSubmit({ title, description, category }); }
  return (
    <form onSubmit={handle} className="card p-5 space-y-3">
      <div>
        <label className="block text-xs text-gray-500 mb-1">Title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required
               className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent focus:outline-none focus:border-tnh-accent"
               placeholder="What's broken or confusing?" />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={4000} required rows={5}
                  className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent focus:outline-none focus:border-tnh-accent"
                  placeholder="Steps to reproduce, error messages, what you expected vs. what happened." />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Category</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent focus:outline-none focus:border-tnh-accent">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
        <button type="submit" disabled={busy} className="btn-primary">{busy ? 'Diagnosing…' : 'Submit (AI will diagnose)'}</button>
      </div>
    </form>
  );
}

function TicketModal({ me, ticket, onClose, onDecide, busy }: { me: Me; ticket: SupportTicket; onClose: () => void; onDecide: (t: SupportTicket, a: 'approve' | 'reject' | 'close') => void; busy: boolean }) {
  const isOpen = ticket.status === 'awaiting_approval' || ticket.status === 'analyzing' || ticket.status === 'new';
  const canApprove = me.role === 'super_admin' && isOpen;
  const canClose = (ticket.created_by_email === me.email || me.role === 'super_admin') && isOpen;
  let fixAction: any = null;
  try { fixAction = ticket.ai_fix_action_json ? JSON.parse(ticket.ai_fix_action_json) : null; } catch {}
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex gap-2 items-center flex-wrap">
              {statusPill(ticket.status)}{sourceBadge(ticket.source)}
              {ticket.needs_super_admin === 1 && <span className="pill-warn">needs super-admin</span>}
            </div>
            <h2 className="font-semibold text-xl mt-2">{ticket.title}</h2>
            <div className="text-xs text-gray-500 mt-1">
              {ticket.category} · by {ticket.created_by_email} · {new Date(ticket.created_at).toLocaleString()}
            </div>
            {ticket.related_qa_finding_id && (
              <div className="text-xs text-gray-500 mt-1">QA finding: <span className="font-mono">{ticket.related_qa_finding_id}</span></div>
            )}
          </div>
          <button onClick={onClose} className="btn-ghost text-sm">Close</button>
        </div>
        <div className="text-sm whitespace-pre-wrap mb-4">{ticket.description}</div>
        {(ticket.ai_diagnosis || ticket.ai_root_cause || ticket.ai_proposed_fix || fixAction) && (
          <div className="p-4 rounded-lg bg-tnh-accent/5 border border-tnh-accent/20 text-sm space-y-2 mb-4">
            <div className="text-[10px] uppercase tracking-wider text-tnh-accent font-semibold">AI analysis</div>
            {ticket.ai_diagnosis && <div><b>Diagnosis:</b> {ticket.ai_diagnosis}</div>}
            {ticket.ai_root_cause && <div><b>Root cause:</b> {ticket.ai_root_cause}</div>}
            {ticket.ai_proposed_fix && <div><b>Proposed fix:</b> {ticket.ai_proposed_fix}</div>}
            {fixAction && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-gray-500">Structured action</summary>
                <pre className="mt-1 p-2 bg-gray-50 dark:bg-gray-900 rounded text-[11px] overflow-x-auto font-mono">{JSON.stringify(fixAction, null, 2)}</pre>
              </details>
            )}
          </div>
        )}
        {ticket.needs_super_admin === 1 && (
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-tnh-warn text-xs text-tnh-warn mb-4">
            This ticket touches access, permissions, or sensitive data. Approval = the super-admin accepts the proposed fix.
            Actual execution still happens manually in this MVP.
          </div>
        )}
        {ticket.fix_result && (
          <div className="text-sm text-gray-500 mb-4"><b>Resolution notes:</b> {ticket.fix_result}</div>
        )}
        {ticket.decided_at && (
          <div className="text-sm text-gray-500 mb-4">
            Decided {new Date(ticket.decided_at).toLocaleString()} by {ticket.decided_by ?? '?'}.
          </div>
        )}
        <div className="flex gap-2 justify-end">
          {canClose && <button onClick={() => onDecide(ticket, 'close')} disabled={busy} className="btn-ghost">Close</button>}
          {canApprove && <button onClick={() => onDecide(ticket, 'reject')} disabled={busy} className="btn-ghost text-tnh-err">Reject fix</button>}
          {canApprove && <button onClick={() => onDecide(ticket, 'approve')} disabled={busy} className="btn-primary">Approve fix</button>}
        </div>
      </div>
    </div>
  );
}
